import { isObject, isString } from 'lodash'

import type {
  AssistantAttachment as GeneratedAssistantAttachment,
  AssistantMessage as PersistedAssistantMessage
} from '~/data/api/client'

/**
 * Validated attachment metadata for UI use.
 *
 * Field names match the generated {@link GeneratedAssistantAttachment}; OpenAPI leaves every
 * property optional, so after parse we require the fields the chips and send path read.
 */
export type AssistantAttachment = Required<
  Pick<GeneratedAssistantAttachment, 'attachmentId' | 'filename' | 'contentType' | 'sizeBytes'>
> &
  Pick<GeneratedAssistantAttachment, 'createdAt'>

export type AttachmentErrorCode = 'tooLarge' | 'unsupportedType' | 'tooMany' | 'uploadFailed'

/** A file the user picked, tracked from validation through upload until it is sent with a message. */
export type PendingAttachment = {
  localId: string
  file: File
  status: 'uploading' | 'ready' | 'error'
  /** 0–100 upload progress; 100 once the server has answered. */
  progress: number
  attachment?: AssistantAttachment
  error?: AttachmentErrorCode
}

/** An attachment as shown on a sent user bubble; `previewUrl` exists only for images picked in this tab. */
export type MessageAttachment = AssistantAttachment & { previewUrl?: string }

export const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024
export const ATTACHMENT_MAX_COUNT = 5

/** Content types the upload endpoint accepts (contract §7). */
export const ATTACHMENT_CONTENT_TYPES: readonly string[] = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'text/csv',
  'text/plain',
  'text/markdown',
  'application/json',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
]

/**
 * Browsers report CSV/XLSX/Markdown inconsistently (`application/octet-stream`, `application/vnd.ms-excel`, or
 * nothing at all), so the extension is the reliable signal for those; the backend re-derives the type from it.
 */
const ATTACHMENT_EXTENSIONS: Readonly<Record<string, string>> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  gif: 'image/gif',
  csv: 'text/csv',
  txt: 'text/plain',
  md: 'text/markdown',
  json: 'application/json',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
}

/** `accept` attribute for the hidden file input: every allowed MIME type plus the extensions. */
export const ATTACHMENT_ACCEPT = [
  ...ATTACHMENT_CONTENT_TYPES,
  ...Object.keys(ATTACHMENT_EXTENSIONS).map((extension) => `.${extension}`)
].join(',')

function extensionOf(filename: string): string {
  const index = filename.lastIndexOf('.')
  return index >= 0 ? filename.slice(index + 1).toLowerCase() : ''
}

/** The content type the backend will accept for this file, or undefined when neither MIME nor extension is allowed. */
export function resolveAttachmentContentType(file: Pick<File, 'name' | 'type'>): string | undefined {
  const byExtension = ATTACHMENT_EXTENSIONS[extensionOf(file.name)]
  if (byExtension) return byExtension
  return ATTACHMENT_CONTENT_TYPES.includes(file.type) ? file.type : undefined
}

export function isImageAttachment(contentType: string): boolean {
  return contentType.startsWith('image/')
}

/** Client-side pre-check mirroring the backend's 413/415 rules so obvious rejects never leave the browser. */
export function validateAttachmentFile(file: Pick<File, 'name' | 'type' | 'size'>): AttachmentErrorCode | null {
  if (!resolveAttachmentContentType(file)) return 'unsupportedType'
  if (file.size > ATTACHMENT_MAX_BYTES) return 'tooLarge'
  return null
}

/**
 * Narrows a generated (or JSON-parsed) attachment to the fields the UI requires.
 * Entries missing `attachmentId` / `filename` are dropped.
 */
export function parseAssistantAttachment(value: GeneratedAssistantAttachment | unknown): AssistantAttachment | null {
  if (!isObject(value)) return null
  const record = value as Record<string, unknown>
  if (!isString(record.attachmentId) || !isString(record.filename)) return null
  return {
    attachmentId: record.attachmentId,
    filename: record.filename,
    contentType: isString(record.contentType) ? record.contentType : 'application/octet-stream',
    sizeBytes: typeof record.sizeBytes === 'number' ? record.sizeBytes : 0,
    createdAt: isString(record.createdAt) ? record.createdAt : undefined
  }
}

/** Maps `AssistantMessage.attachments` through light validation; returns undefined when none survive. */
export function attachmentsFromPersistedMessage(
  message: Pick<PersistedAssistantMessage, 'attachments'>
): AssistantAttachment[] | undefined {
  const raw = message.attachments
  if (!raw?.length) return undefined
  const parsed = raw.map(parseAssistantAttachment).filter((entry): entry is AssistantAttachment => entry !== null)
  return parsed.length > 0 ? parsed : undefined
}

/** "12 KB" / "1.4 MB" for chips; sizes come from the browser so binary units match what the OS shows. */
export function formatAttachmentSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
